// -----------------------------------------------------------------------------
// preflight/detect.ts — the capability probe.
//
// Runs once, on entry, before any editor code loads. It asks the browser what it
// can actually do (decode, encode, WebGL2, Web Audio, storage, RAM) and returns
// a single Capabilities object. main.ts uses the `tier` field to route the user
// into the editor (full / degraded) or the "blocked" screen, and the rest of the
// object drives runtime config (export path, proxy resolution, OPFS vs IDB).
//
// NOTE: this is adapted from the reference in CLAUDE.md §5, with the codec probes
// hardened. The original probed H.264 with `avc1.42E01E` (Baseline @ Level 3.0)
// at 1920×1080 — but Level 3.0 only permits ~720p, so capable 1080p encoders
// correctly returned "unsupported", and the gate FALSELY reported "no H.264
// encode" on perfectly capable PCs. We now probe with level-appropriate codec
// strings (and several profiles), and treat software-only encode as full-tier
// (only the FFmpeg.wasm fallback is genuinely "degraded").
// -----------------------------------------------------------------------------
export type Tier = 'full' | 'degraded' | 'blocked';

export interface Capabilities {
  tier: Tier;
  webgl2: boolean; webgpu: boolean; webAudio: boolean;
  videoDecode: boolean; videoEncodeHW: boolean; videoEncodeSW: boolean; videoEncodeVP9: boolean;
  audioEncodeAAC: boolean; audioEncodeOpus: boolean;
  opfs: boolean; fsAccess: boolean; crossOriginIsolated: boolean;
  deviceMemoryGB: number | null; cores: number | null; storageQuotaGB: number | null;
  reasons: string[];
}

type HwPref = 'prefer-hardware' | 'prefer-software' | 'no-preference';
interface CodecProbe { codec: string; width: number; height: number }

// H.264 candidates whose LEVEL actually permits the test resolution. Level 4.0
// (0x28) covers 1080p30; 3.1 (0x1f) covers 720p. We try High → Main → Baseline.
const H264_ENCODE: CodecProbe[] = [
  { codec: 'avc1.640028', width: 1920, height: 1080 }, // High @ 4.0
  { codec: 'avc1.4D0028', width: 1920, height: 1080 }, // Main @ 4.0
  { codec: 'avc1.420028', width: 1920, height: 1080 }, // Baseline @ 4.0
  { codec: 'avc1.640032', width: 1920, height: 1080 }, // High @ 5.0 (fallback)
  { codec: 'avc1.64001F', width: 1280, height: 720 },  // High @ 3.1 (720p fallback)
  { codec: 'avc1.42001F', width: 1280, height: 720 },  // Baseline @ 3.1
];
// Same list, used for the decode check (decoders are usually more permissive).
const H264_DECODE = ['avc1.640028', 'avc1.4D0028', 'avc1.420028', 'avc1.42E01E'];
// VP9 candidates for the WebM export path (e.g. Firefox, which lacks H.264 encode).
const VP9_ENCODE: CodecProbe[] = [
  { codec: 'vp09.00.40.08', width: 1920, height: 1080 }, // profile 0, level 4.0, 8-bit
  { codec: 'vp09.00.41.08', width: 1920, height: 1080 }, // level 4.1 (Firefox may want this)
  { codec: 'vp09.00.30.08', width: 1280, height: 720 },  // 720p fallback
];
const AAC = 'mp4a.40.2';

async function canDecodeVideo(): Promise<boolean> {
  if (!('VideoDecoder' in window)) return false;
  for (const codec of H264_DECODE) {
    try {
      const s = await VideoDecoder.isConfigSupported({ codec, codedWidth: 1920, codedHeight: 1080 });
      if (s.supported) return true;
    } catch { /* try next */ }
  }
  return false;
}

// True if ANY level-appropriate H.264 config is supported for the given hint.
async function canEncodeH264(pref: HwPref): Promise<boolean> {
  if (!('VideoEncoder' in window)) return false;
  for (const c of H264_ENCODE) {
    try {
      const s = await VideoEncoder.isConfigSupported({
        codec: c.codec, width: c.width, height: c.height,
        bitrate: 8_000_000, framerate: 30, hardwareAcceleration: pref,
      });
      if (s.supported) return true;
    } catch { /* try next */ }
  }
  return false;
}

async function canEncodeVP9(): Promise<boolean> {
  if (!('VideoEncoder' in window)) return false;
  for (const c of VP9_ENCODE) {
    try {
      const s = await VideoEncoder.isConfigSupported({
        codec: c.codec, width: c.width, height: c.height,
        bitrate: 8_000_000, framerate: 30, hardwareAcceleration: 'no-preference',
      });
      if (s.supported) return true;
    } catch { /* try next */ }
  }
  return false;
}

async function canEncodeAudio(codec: string): Promise<boolean> {
  if (!('AudioEncoder' in window)) return false;
  try {
    const s = await AudioEncoder.isConfigSupported({ codec, sampleRate: 48000, numberOfChannels: 2, bitrate: 128_000 });
    return !!s.supported;
  } catch { return false; }
}
function hasWebGL2(): boolean {
  try { return !!document.createElement('canvas').getContext('webgl2'); } catch { return false; }
}
async function hasWebGPU(): Promise<boolean> {
  if (!('gpu' in navigator)) return false;
  try { return !!(await (navigator as any).gpu.requestAdapter()); } catch { return false; }
}

export async function detectCapabilities(): Promise<Capabilities> {
  const reasons: string[] = [];
  const webgl2 = hasWebGL2();
  const webgpu = await hasWebGPU();
  const webAudio = 'AudioContext' in window || 'webkitAudioContext' in window;
  const videoDecode = await canDecodeVideo();
  const videoEncodeHW = await canEncodeH264('prefer-hardware');
  const videoEncodeSW = videoEncodeHW || await canEncodeH264('prefer-software');
  const videoEncodeVP9 = await canEncodeVP9();
  const audioEncodeAAC = await canEncodeAudio(AAC);
  const audioEncodeOpus = await canEncodeAudio('opus');
  const opfs = !!(navigator.storage && 'getDirectory' in navigator.storage);
  const fsAccess = 'showOpenFilePicker' in window;
  const coi = (self as any).crossOriginIsolated === true;
  const deviceMemoryGB = (navigator as any).deviceMemory ?? null;
  const cores = navigator.hardwareConcurrency ?? null;

  let storageQuotaGB: number | null = null;
  try { const est = await navigator.storage?.estimate?.(); if (est?.quota) storageQuotaGB = +(est.quota / 1e9).toFixed(1); } catch {}

  // A native video encode path exists if we can encode H.264 (MP4) OR VP9 (WebM).
  const nativeVideoEncode = videoEncodeSW || videoEncodeVP9;

  let tier: Tier = 'full';
  if (!webgl2)      { reasons.push('No WebGL2 — preview compositor cannot run.'); tier = 'blocked'; }
  if (!videoDecode) { reasons.push('No WebCodecs H.264 decode — media cannot play.'); tier = 'blocked'; }
  if (!webAudio)    { reasons.push('No Web Audio — audio cannot be processed.'); tier = 'blocked'; }
  if (tier !== 'blocked') {
    // Only the FFmpeg.wasm fallback is genuinely "degraded". Software-only native
    // encode is fully usable, so it stays full-tier (no scary banner).
    if (!nativeVideoEncode) {
      reasons.push('No native video encode — export uses FFmpeg.wasm (slower).');
      tier = 'degraded';
    }
    if (!audioEncodeAAC && !audioEncodeOpus) {
      reasons.push('No native audio encode — audio export via FFmpeg.wasm.');
      tier = 'degraded';
    }
    if ((deviceMemoryGB ?? 8) < 4) {
      reasons.push('Low device memory — preview will use a lower-resolution proxy.');
      tier = 'degraded';
    }
  }
  return { tier, webgl2, webgpu, webAudio, videoDecode, videoEncodeHW, videoEncodeSW, videoEncodeVP9,
           audioEncodeAAC, audioEncodeOpus, opfs, fsAccess, crossOriginIsolated: coi,
           deviceMemoryGB, cores, storageQuotaGB, reasons };
}
